import { useState } from "react";
import { adminHttp, resolveImageUrl } from "@/lib/api";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * ImageUploader — manages an array of image URLs (strings).
 * - Drag & drop / click to upload (multipart) → posts to /api/admin/upload
 * - Or paste an external URL
 * - Shows thumbnails with remove button
 */
export default function ImageUploader({ value = [], onChange, max = 8, testId = "images" }) {
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const next = [...value];
    try {
      for (const file of files) {
        if (next.length >= max) {
          toast.error(`Maximum ${max} images.`);
          break;
        }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await adminHttp.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push(data.url);
      }
      onChange(next);
      toast.success("Uploaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const addUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    if (value.length >= max) {
      toast.error(`Maximum ${max} images.`);
      return;
    }
    onChange([...value, u]);
    setUrlInput("");
  };

  const removeAt = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div data-testid={`uploader-${testId}`} className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {value.map((url, i) => (
          <div
            key={i}
            data-testid={`uploader-${testId}-image-${i}`}
            className="relative aspect-[4/5] bg-[#ede5d5] border border-[#1c1c1c]/10 overflow-hidden group"
          >
            <img src={resolveImageUrl(url)} alt={`img-${i}`} className="w-full h-full object-cover" />
            <button
              type="button"
              data-testid={`uploader-${testId}-remove-${i}`}
              onClick={() => removeAt(i)}
              className="absolute top-2 right-2 w-7 h-7 bg-black/70 backdrop-blur-md flex items-center justify-center text-[#1c1c1c] hover:bg-[#c5a059] hover:text-black transition-colors"
              aria-label="Remove image"
            >
              <X size={14} />
            </button>
          </div>
        ))}

        {value.length < max && (
          <label
            data-testid={`uploader-${testId}-dropzone`}
            className={`relative aspect-[4/5] flex flex-col items-center justify-center gap-2 border border-dashed border-[#c5a059]/30 cursor-pointer hover:border-[#c5a059] hover:bg-[#ede5d5] transition-colors ${
              uploading ? "opacity-60 pointer-events-none" : ""
            }`}
          >
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              data-testid={`uploader-${testId}-file-input`}
              onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
            />
            {uploading ? (
              <Loader2 size={20} className="text-[#c5a059] animate-spin" />
            ) : (
              <>
                <Upload size={20} className="text-[#c5a059]" />
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#6a6259]">
                  Upload
                </span>
              </>
            )}
          </label>
        )}
      </div>

      <div className="flex items-center gap-3">
        <input
          data-testid={`uploader-${testId}-url-input`}
          type="text"
          placeholder="Or paste an image URL…"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          className="editorial-input !py-2.5 flex-1"
        />
        <button
          type="button"
          data-testid={`uploader-${testId}-url-add`}
          onClick={addUrl}
          className="px-5 py-2.5 border border-[#c5a059]/60 text-[#c5a059] uppercase text-[10px] tracking-[0.28em] hover:bg-[#c5a059] hover:text-black transition-colors"
        >
          Add URL
        </button>
      </div>
    </div>
  );
}
